import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { query } from '@warp-drive/utilities/json-api';
import {
  deriveSlrLoanStandingAction,
  deriveSlrLoanStandingStatus,
  normalizeSlrGoodStanding,
} from 'tio-employee/utils/slr-loan-standing-verification';
import {
  createEmployeePlanAccountDocumentRequest,
  confirmSlrLoanStandingNameMatchRequest,
} from 'tio-data/requests/slr-loan-standing-verification';
import type { Store } from '@warp-drive/core';
import type SessionContextService from 'tio-employee/services/session-context';
import type ObservablesService from 'tio-common/services/observables';
import type CableService from 'tio-common/services/cable';
import type { UploadFile } from 'ember-file-upload';
import type { EmployeePlanAccount } from 'tio-data/data/resources/employee-plan-account.type';
import type {
  SlrGoodStandingReason,
  SlrLoanStandingAuditState,
  SlrLoanStandingCablePayload,
  SlrLoanStandingStatus,
} from 'tio-employee/types/slr-loan-standing-verification';

// Order a problem EPA is surfaced when the employee has several and none are
// eligible — most blocking first. (`processing` is client-only and never
// derived here, but listed for completeness.)
const ATTENTION_PRIORITY: SlrLoanStandingStatus[] = [
  'ineligible',
  'cannot_determine',
  'try_again',
  'pending_upload',
  'unknown',
  'processing',
];

/**
 * Lifecycle of the one in-flight verification upload (Figma v3 767-7670 —
 * the inline document row owns upload + realtime feedback; no drawer):
 * `submitting` covers the create chain (presign → S3 → document → join row),
 * `processing` the AI document pipeline, `verifying` the eligibility
 * evaluation after reflection landed. `settled` carries the verdict in
 * `outcome`; `timeout` means the window elapsed without OUR document's
 * verdict; `error` is a failed create chain or an unreadable document.
 */
export type SlrLiveUploadPhase =
  'idle' | 'submitting' | 'processing' | 'verifying' | 'settled' | 'timeout' | 'error';

export type SlrLiveUploadErrorKind = 'upload_failed' | 'invalid_document' | 'processing_failed';

export interface SlrLiveUpload {
  phase: SlrLiveUploadPhase;
  /** Original filename of the submitted upload — anchors every banner to the file. */
  filename: string | null;
  /** The settled verdict, presentation-derived — only on `phase: 'settled'`. */
  outcome: SlrLoanStandingAuditState | null;
  errorKind: SlrLiveUploadErrorKind | null;
}

const IDLE_LIVE_UPLOAD: SlrLiveUpload = {
  phase: 'idle',
  filename: null,
  outcome: null,
  errorKind: null,
};

const LIVE_UPLOAD_BUSY_PHASES: ReadonlySet<SlrLiveUploadPhase> = new Set([
  'submitting',
  'processing',
  'verifying',
]);

// The verdict rides the full Observatory pipeline (AI extraction → reflection
// → evaluation), so the window is generous — the fallback refetches the
// settled state rather than declaring failure.
const VERDICT_TIMEOUT_MS = 60_000;

interface ObservableDocumentBatchStatus {
  recordAttributes?: { extractionState?: string };
  transition?: { event?: string };
}

export default class SlrLoanStandingVerificationService extends Service {
  @service declare store: Store;
  @service declare sessionContext: SessionContextService;
  @service declare observables: ObservablesService;
  @service declare cable: CableService;

  /**
   * Read path (tio-front-end#8714): the signed-in employee's current
   * good-standing audit state, read from the JSON:API `employee_plan_accounts`
   * resource (auth-scoped to the user) — replacing the bespoke
   * `/people/:id/slr-loan-standing-verification/current` endpoint that never
   * existed.
   *
   * Account scoping (tio-front-end#9428): alerts/messaging relate only to the
   * account receiving employer contributions — EPAs on the employee's other
   * linked accounts are excluded server-side via `filter[account_id]`. The
   * scope id is the plain `accountReceivingContributionsId` person attribute,
   * which is serialized with the session person from `/me` (no accounts
   * include required — unlike the derived `accountReceivingContributions`
   * getter, which needs materialized accounts and goes null for paid-off /
   * deactivated accounts). When no receiving account exists, there is nothing
   * to message about: returns null WITHOUT falling back to other accounts'
   * EPAs.
   *
   * Multi-EPA aggregation (within the receiving account): if ANY in-scope EPA
   * is `eligible` we surface that; otherwise the most blocking attention
   * state. Returns null when there are no in-scope EPAs.
   *
   * Deliberately NOT filtered by employee_plan_id: the session exposes the
   * contribution plan (`currentEmployee.plan.id`), not the EmployeePlan
   * join-row id that `filter[employee_plan_id]` matches — sending the plan id
   * silently excluded every real EPA and the alert never fired (the
   * reliability bug this replaced). The account filter plus the API's
   * auth-scoping to the signed-in user is the load-bearing scope; multiple
   * EPAs on the account aggregate below.
   */
  fetchCurrentState = async (options?: { reload?: boolean }) => {
    const receivingAccountId = this.sessionContext.person?.accountReceivingContributionsId;
    if (!receivingAccountId) return null;

    const { content } = (await this.store.request(
      query<EmployeePlanAccount>(
        'employee-plan-account',
        {
          'filter[account_id]': receivingAccountId,
          // The account carries the reflected observation whose borrowerName
          // the eval compared — surfaced as documentName for the Review Name
          // panel (Figma v3). The provenance chain feeds the name-match
          // card's filename callout (#10131 review: relationship, not a
          // flattened attribute).
          include: 'account,good-standing-source-document.observable-asset',
        },
        // Post-write readers pass reload to bypass the soft-cache window —
        // the PATCH is SkipCache, so it never invalidates this query's
        // cached document.
        options?.reload ? { reload: true } : {}
      )
    )) as { content: { data: EmployeePlanAccount[] } };

    const person = this.sessionContext.person;
    const employerProvidedName =
      [person?.firstName, person?.lastName].filter(Boolean).join(' ') || null;

    return aggregateAuditState(
      content.data.map((epa) => toAuditState(epa, { employerProvidedName }))
    );
  };

  /**
   * Upload path (tio-api#9814, the #7640 backend-owns-the-chain contract):
   * two JSON:API writes. The document rides the shared Observatory create
   * (`ObservablesService` — presign, S3 PUT, observable-asset,
   * observable-document), then one `employee-plan-account-documents` join row
   * names the EPA this upload verifies. The backend takes it from there:
   * reflection evaluates the referenced EPA and
   * `Slr::VerificationOutcomeBroadcaster` pushes the raw verdict on
   * `ServiceResponseChannel` keyed by the returned `assetUuid` — subscribe
   * with it, and fall back to `fetchCurrentState({ reload: true })` on
   * timeout.
   */
  createVerificationDocument = async (
    employeePlanAccountId: string,
    uploadFile: UploadFile,
    options: { batch?: string } = {}
  ) => {
    const person = this.sessionContext.person;
    if (!person) {
      throw new Error(
        '[SLR loan standing] Cannot upload a verification document without a session person'
      );
    }

    const document = await this.observables.createObservableDocument(uploadFile, {
      person,
      batch: options.batch,
    });
    if (!document.id) {
      throw new Error('[SLR loan standing] Observable document saved without an id');
    }

    await this.store.request(
      createEmployeePlanAccountDocumentRequest(employeePlanAccountId, document.id)
    );

    return {
      documentId: document.id,
      assetUuid: document.observableAsset?.uuid ?? null,
    };
  };

  /**
   * Record the participant's answer to the fuzzy name-match prompt
   * (`name_match_confirmation_required`) by PATCHing `name_match_confirmation`
   * on the EPA (tio-api#8054). The PATCH re-evaluates good standing inline
   * server-side, but its response is rendered from the pre-eval record — so
   * refetch (reload: the PATCH is SkipCache and never invalidates the query's
   * cached document) for the settled state, falling back to deriving from the
   * PATCH response if the refetch comes back empty. That response is a RAW
   * JSON:API resource (`{ id, attributes }`), not a materialized record —
   * unwrap it before deriving.
   */
  confirmNameMatch = async (employeePlanAccountId: string, confirmed: boolean) => {
    const { content } = (await this.store.request(
      confirmSlrLoanStandingNameMatchRequest(
        employeePlanAccountId,
        confirmed ? 'confirmed' : 'denied'
      )
    )) as { content: { data: { id: string | null; attributes: Record<string, unknown> } } };

    const settled = await this.fetchCurrentState({ reload: true });
    if (settled) return settled;

    const raw = content.data;
    return toAuditState({
      id: raw.id,
      ...(raw.attributes as Partial<EmployeePlanAccount>),
    } as EmployeePlanAccount);
  };

  // ---------------------------------------------------------------------
  // Live upload (Figma v3 767-7670): the inline document row submits and the
  // SERVICE owns the realtime lifecycle — cable subscriptions, the verdict
  // timeout, and the tracked state any surface renders from. Previously this
  // machine lived in the upload drawer; hoisting it here survives the row's
  // re-renders and keeps exactly one in-flight verification per session.
  // ---------------------------------------------------------------------

  @tracked liveUpload: SlrLiveUpload = IDLE_LIVE_UPLOAD;

  #batchSubscriptionParams: object | null = null;
  #verdictSubscriptionParams: object | null = null;
  #verdictTimer?: ReturnType<typeof setTimeout>;
  #submittedEpaId: string | null = null;
  #submittedDocumentId: string | null = null;
  #submittedAssetUuid: string | null = null;
  #onSettled: (() => void) | null = null;

  get isLiveUploadBusy() {
    return LIVE_UPLOAD_BUSY_PHASES.has(this.liveUpload.phase);
  }

  willDestroy() {
    super.willDestroy();
    this.#teardownRealtime();
  }

  /**
   * Submit a verification document and track it to a settled verdict. The
   * returned promise resolves when the CREATE CHAIN finishes — the verdict
   * arrives later through `liveUpload` (cable push, or the timeout refetch).
   * `onSettled` fires once the verdict (or the timeout-accepted refetch) is
   * applied, so the caller can refresh its route-level state.
   */
  submitVerificationDocument = async (
    employeePlanAccountId: string,
    uploadFile: UploadFile,
    options: { verdictTimeoutMs?: number; onSettled?: () => void } = {}
  ) => {
    // One verification at a time: the row's control is disabled while busy,
    // and a concurrent submit would cross-wire the subscriptions.
    if (this.isLiveUploadBusy) return;

    this.#teardownRealtime();
    this.#onSettled = options.onSettled ?? null;
    this.#submittedEpaId = employeePlanAccountId;
    const filename = uploadFile.file.name;
    this.liveUpload = { phase: 'submitting', filename, outcome: null, errorKind: null };

    const batchId = crypto.randomUUID();
    this.#subscribeToBatchStatus(batchId);

    try {
      const { documentId, assetUuid } = await this.createVerificationDocument(
        employeePlanAccountId,
        uploadFile,
        { batch: batchId }
      );
      // The batch channel is live throughout the create chain, so a fast
      // INVALID/FAILED may have terminal-errored the upload during the await
      // — never resurrect it, and never downgrade an early 'verifying'
      // (content_reflected while still 'submitting').
      if (!LIVE_UPLOAD_BUSY_PHASES.has(this.liveUpload.phase)) return;
      this.#submittedDocumentId = documentId;
      this.#submittedAssetUuid = assetUuid;
      // The earliest possible broadcast is the post-reflection evaluation,
      // minutes of pipeline away — subscribing right after the create chain
      // returns cannot miss it; the timeout refetch covers a lost broadcast.
      if (assetUuid) this.#subscribeToVerdict(assetUuid);
      if (this.liveUpload.phase === 'submitting') {
        this.liveUpload = { ...this.liveUpload, phase: 'processing' };
      }
      this.#armVerdictTimeout(options.verdictTimeoutMs);
    } catch (error) {
      console.error('[SLR loan standing] Failed to submit verification document', error);
      this.#teardownRealtime();
      // A batch-driven terminal error during the await is more specific than
      // the generic chain failure — keep the first verdict.
      if (this.liveUpload.phase !== 'error') {
        this.liveUpload = { phase: 'error', filename, outcome: null, errorKind: 'upload_failed' };
      }
    }
  };

  /** Clear a settled/timed-out/errored upload back to the idle row. */
  dismissLiveUpload = () => {
    this.#teardownRealtime();
    this.liveUpload = IDLE_LIVE_UPLOAD;
  };

  #unsubscribeBatch = () => {
    if (!this.#batchSubscriptionParams) return;
    this.cable.unsubscribe(this.#batchSubscriptionParams);
    this.#batchSubscriptionParams = null;
  };

  #unsubscribeVerdict = () => {
    if (!this.#verdictSubscriptionParams) return;
    this.cable.unsubscribe(this.#verdictSubscriptionParams);
    this.#verdictSubscriptionParams = null;
  };

  #teardownRealtime = () => {
    this.#unsubscribeBatch();
    this.#unsubscribeVerdict();
    clearTimeout(this.#verdictTimer);
  };

  /**
   * Document-pipeline progress, keyed by the upload batch (the same channel
   * the statement-upload drawer watches): surfaces document-level failures —
   * unreadable/invalid files — with specific copy long before the verdict,
   * and flips the row to "verifying" once reflection lands.
   */
  #subscribeToBatchStatus = (batchId: string) => {
    const channelParams = { channel: 'ObservableDocumentBatchStatusChannel', batch: batchId };
    this.#batchSubscriptionParams = channelParams;

    this.cable.subscribe(channelParams, {
      received: (data: unknown) => {
        const message = data as ObservableDocumentBatchStatus;
        const extractionState = message.recordAttributes?.extractionState ?? '';
        const event = message.transition?.event ?? '';

        if (['ExtractionState.FAILED', 'ExtractionState.INVALID'].includes(extractionState)) {
          this.#teardownRealtime();
          this.liveUpload = {
            ...this.liveUpload,
            phase: 'error',
            errorKind:
              extractionState === 'ExtractionState.INVALID'
                ? 'invalid_document'
                : 'processing_failed',
          };
          return;
        }

        if (event === 'reflection_failed') {
          this.#teardownRealtime();
          this.liveUpload = { ...this.liveUpload, phase: 'error', errorKind: 'processing_failed' };
          return;
        }

        if (event === 'content_reflected' && LIVE_UPLOAD_BUSY_PHASES.has(this.liveUpload.phase)) {
          // Document read; the eligibility verdict now rides the
          // ServiceResponseChannel broadcast (or the timeout fallback). The
          // event can land while the create chain is still 'submitting' —
          // accept it from any busy phase.
          this.liveUpload = { ...this.liveUpload, phase: 'verifying' };
        }
      },
    });
  };

  /**
   * The settled verdict, broadcast raw by tio-api's
   * Slr::VerificationOutcomeBroadcaster on the uploaded asset's stream —
   * presentation is derived client-side so the status/action mapping lives in
   * exactly one place (the utils every SLR surface already uses).
   */
  #subscribeToVerdict = (assetUuid: string) => {
    const channelParams = { channel: 'ServiceResponseChannel', request_id: assetUuid };
    this.#verdictSubscriptionParams = channelParams;

    this.cable.subscribe(channelParams, {
      received: (data: unknown) => {
        this.#handleVerdictPayload(data as SlrLoanStandingCablePayload);
      },
    });
  };

  #handleVerdictPayload = (payload: SlrLoanStandingCablePayload) => {
    if (
      payload.employeePlanAccountId &&
      this.#submittedEpaId &&
      payload.employeePlanAccountId !== this.#submittedEpaId
    ) {
      return;
    }
    if (
      payload.assetUuid &&
      this.#submittedAssetUuid &&
      payload.assetUuid !== this.#submittedAssetUuid
    ) {
      return;
    }
    if (!payload.goodStanding) return;

    // Normalize, never cast: a new/malformed backend value must degrade to
    // 'unknown' (matching the service read path) rather than derive an
    // undefined status and an undefined translation key.
    const goodStanding = normalizeSlrGoodStanding(payload.goodStanding);
    const reason = (payload.goodStandingReason ?? null) as SlrGoodStandingReason | null;

    this.#applyOutcome({
      employeePlanAccountId: payload.employeePlanAccountId ?? this.#submittedEpaId,
      status: deriveSlrLoanStandingStatus(goodStanding, reason),
      action: deriveSlrLoanStandingAction(goodStanding, reason),
      primaryReason: payload.goodStandingReason ?? null,
      reasons: payload.goodStandingReasons,
      sourceDocumentId: payload.sourceDocumentId ?? this.#submittedDocumentId,
      sourceDocumentFilename: this.liveUpload.filename,
      featureEnabled: true,
    });
  };

  #applyOutcome = (state: SlrLoanStandingAuditState) => {
    this.#teardownRealtime();
    this.liveUpload = { ...this.liveUpload, phase: 'settled', outcome: state, errorKind: null };
    this.#onSettled?.();
  };

  /**
   * A lost broadcast must never strand the row on "processing" (the TAS
   * analysis watcher's lesson): after the window, refetch the settled state.
   * If OUR document produced the current verdict, show it; otherwise be
   * honest that processing is still running and let the user move on.
   */
  #armVerdictTimeout = (verdictTimeoutMs?: number) => {
    clearTimeout(this.#verdictTimer);
    this.#verdictTimer = setTimeout(() => {
      void this.#verdictTimeoutFallback();
    }, verdictTimeoutMs ?? VERDICT_TIMEOUT_MS);
  };

  #verdictTimeoutFallback = async () => {
    if (!LIVE_UPLOAD_BUSY_PHASES.has(this.liveUpload.phase)) return;

    let settled: SlrLoanStandingAuditState | null = null;
    try {
      settled = await this.fetchCurrentState({ reload: true });
    } catch (error) {
      console.warn('[SLR loan standing] Timeout refetch failed', error);
    }

    // The cable verdict can land while the refetch is in flight — it already
    // settled the upload (and fired onSettled); a second outcome, or worse a
    // 'timeout' overwrite of a valid verdict, must not follow.
    if (!LIVE_UPLOAD_BUSY_PHASES.has(this.liveUpload.phase)) return;

    if (
      settled &&
      this.#submittedDocumentId &&
      settled.sourceDocumentId === this.#submittedDocumentId
    ) {
      this.#applyOutcome(settled);
      return;
    }

    this.#teardownRealtime();
    this.liveUpload = { ...this.liveUpload, phase: 'timeout' };
  };
}

function toAuditState(
  epa: EmployeePlanAccount,
  context?: { employerProvidedName?: string | null }
): SlrLoanStandingAuditState {
  const goodStanding = normalizeSlrGoodStanding(epa.goodStanding);
  const reason = (epa.goodStandingReason || null) as SlrGoodStandingReason | null;
  const observation = epa.account?.observation as Record<string, unknown> | undefined;
  // Observation-hash keys are dasherized on the wire (cf. account.ext reading
  // 'interest-weighted-average'); camel fallback covers demo fixtures.
  const documentName = observation?.['borrower-name'] ?? observation?.['borrowerName'];

  return {
    employeePlanAccountId: epa.id,
    status: deriveSlrLoanStandingStatus(goodStanding, reason),
    primaryReason: epa.goodStandingReason || null,
    action: deriveSlrLoanStandingAction(goodStanding, reason),
    // Provenance id, not just the filename: the live-upload timeout fallback
    // matches this against the document it submitted to decide whether the
    // refetched verdict is OURS (a lost broadcast) or someone else's eval.
    sourceDocumentId: epa.goodStandingSourceDocument?.id ?? null,
    sourceDocumentFilename: epa.goodStandingSourceDocument?.observableAsset?.filename ?? null,
    featureEnabled: true,
    employerProvidedName: context?.employerProvidedName ?? null,
    documentName: typeof documentName === 'string' && documentName ? documentName : null,
    // Audit-window context (tio-api #9316/#9364). Empty (null / false) for
    // eligible accounts and plans without an audit schedule, so the card's
    // deadline chips + period copy stay dark unless a window applies.
    auditRequiredBy: epa.auditRequiredBy ?? null,
    auditPastDue: epa.auditPastDue ?? false,
    contributionPeriodEnd: epa.contributionPeriodEnd ?? null,
  };
}

function aggregateAuditState(
  states: SlrLoanStandingAuditState[]
): SlrLoanStandingAuditState | null {
  if (states.length === 0) return null;

  const eligible = states.find((state) => state.status === 'eligible');
  if (eligible) return eligible;

  for (const status of ATTENTION_PRIORITY) {
    const match = states.find((state) => state.status === status);
    if (match) return match;
  }

  return states[0] ?? null;
}
